<template>
  <vue-resizable
    ref="resizeBorders"
    class="node"
    :min-width="100"
    :min-height="100"
    :width="state.width"
    :height="state.height"
    :active="state.readonly ? [] : ['r', 'rb', 'b', 'lb', 'l', 'lt', 't', 'rt']"
    @resize:start="onResizeStart"
    @resize:end="onResizeEnd"
  >
    <div class="node group" :class="[selected()]">
      <div class="title group-draggable" :class="{ 'title-collapsed': state.isCollapsed }">
        <div class="title-left">
          <input
            v-if="state.editing"
            ref="titleRef"
            v-model="state.title"
            class="title-input"
            placeholder="Group"
            @input="emitUpdate"
            @blur="blurTitle"
            @keydown="keyDownTitle($event)"
          >
          <div v-show="!state.editing" class="title-static group-draggable">{{ state.title || 'Group' }}</div>
          <md-button 
            class="md-icon-button" 
            :disabled="editor.readonly" 
            @click="editTitle"
          >
            <md-icon class="icon" :class="{ 'icon-collapsed': state.isCollapsed }">mode_edit</md-icon>
          </md-button>
        </div>
        <div class="title-right">
          <md-button class="md-icon-button collapse-expand-button" :disabled="state.readonly" @click="toggleCollapse">
            <img v-if="state.isCollapsed" class="icon" src="../assets/icons/expand-group.svg">
            <img v-else class="icon" src="../assets/icons/collapse-group.svg">
          </md-button>
        </div>
        <md-button 
          v-if="!state.isCollapsed" 
          class="md-icon-button ungroup-button" 
          @click="ungroup"
        >
          <img class="icon" src="../assets/icons/ungroup.svg">
        </md-button>
      </div>
      <div class="node-body" :class="nodeBodyClasses">
        <span v-if="state.isCollapsed" class="md-caption node-body-text">Do not place nodes in this area</span>
        <br>
        <span v-if="state.isCollapsed" class="md-caption node-body-text">{{ collapsedNodeText }}</span>
      </div>
    </div>
  </vue-resizable>
</template>

<script>
import VueResizable from 'vue-resizable';
import { uniq } from 'lodash';
import ReteVueRenderPlugin from 'rete-vue-render-plugin';
import getEditorCoords from '../utils/getEditorCoords';

export default {
  name: 'group',
  components: {
    VueResizable,
  },
  mixins: [ReteVueRenderPlugin.mixin],
  props: {},
  data() {
    return {
      state: {
        editing: false,
        firstCreate: true,
        previousTitle: this.node.data.title || '',
        width: this.node.data.width || 400,
        height: this.node.data.height || 400,
        title: this.node.data.title || '',
        childIds: this.node.data.childIds || [],
        isCollapsed: this.node.data.isCollapsed || false,
        readonly: false,
      },
    };
  },
  computed: {
    nodeBodyClasses() {
      return {
        'node-drag-target': this.node.meta.isDragTarget,
        'node-body-collapsed': this.state.isCollapsed
      };
    },
    collapsedNodeText() {
      const numChildren = this.state.childIds.length;
      return `${numChildren} collapsed node${numChildren === 1 ? '' : 's'} contained here`;
    },
  },
  created() {
    this.state.readonly = this.editor.readonly;
    this.editor.on('readonly', readonly => {
      this.state.readonly = readonly;
    });
  },
  mounted() {
    this.node.zIndex = this.state.isCollapsed ? 5 : -2;
    this.editor.on('nodecreated', (node) => {
      // Pretty hacky way to focus edit on a newly created frame
      if (node.name === 'Group' && this.state.firstCreate) {
        this.state.firstCreate = false;
        if (node.data.newlyCreated) {
          node.data.newlyCreated = undefined;
          this.state.editing = true;
          setTimeout(() => {
            this.$refs.titleRef.focus();
          }, 500);
        }
      }
    });
  },
  beforeDestroy() {},
  methods: {
    onResizeStart() {
    },
    onResizeEnd(e) {
      const position = getEditorCoords(this.$refs.resizeBorders, this.editor);
      const viewNode = this.editor.view.nodes.get(this.node);
      viewNode.translate(position[0], position[1]);
      this.state.width = e.width;
      this.state.height = e.height;
      this.emitUpdate();
      this.editor.trigger('resizeEnd', this.node);
      // Reset borders now that underlying node has been translated
      this.$refs.resizeBorders.l = 0;
      this.$refs.resizeBorders.t = 0;
    },
    emitUpdate() {
      this.node.data = {
        width: this.state.width,
        height: this.state.height,
        title: this.state.title,
        childIds: uniq(this.state.childIds),
        isCollapsed: this.state.isCollapsed,
      };
      this.editor.trigger('process', this.node);
    },
    editTitle() {
      this.state.previousTitle = this.state.title;
      this.state.editing = true;
      this.$nextTick(() => {
        this.$refs.titleRef.focus();
      });
    },
    toggleCollapse() {
      this.state.isCollapsed = !this.state.isCollapsed;
      this.emitUpdate();
      this.editor.trigger('collapse', {
        node: this.node,
        isCollapsed: this.state.isCollapsed,
      });
    },
    blurTitle() {
      this.state.editing = false;
    },
    keyDownTitle(e) {
      if (e.code === 'Escape') {
        this.state.title = this.state.previousTitle;
      }
      if (e.code === 'Enter' || e.code === 'NumpadEnter' || e.code === 'Escape') {
        this.state.editing = false;
      }
    },
    ungroup() {
      this.editor.removeNode(this.node);
    },
  },
};
</script>

<style scoped>
.node {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.node.selected {
  box-shadow: 0 3px 6px 0 rgba(255, 255, 255, 0.7);
  border: solid 4px #ffffff;
  border-radius: 10px;
}

.title {
  background: #3059C9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 12px;
  margin-bottom: -1px;
  color: white;
  height: 36px;
  width: 100%;
  font-size: 1.2rem;
  cursor: pointer;
  border-top: solid 6px #3059C9;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.title-collapsed {
  border-top: solid 6px #304786;
  background-color:  #304786;
  color: #9396A0;
}

.title-left {
  display: flex;
  align-items: center;
}

.title-input {
  border: none;
  outline: none;
  background: white;
  font-size: 1.2rem;
}

.node-body {
  z-index: -2;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: solid 6px #3059C9;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  background: #313136;
}

.node-drag-target {
  background-color: blue;
}

.node-body-collapsed {
  background-color: #303143;
  border: dashed 6px #304786;
  border-radius: 0;
  z-index: 5;
}

.node-body-text {
  color: #9396A0;
  line-height: .2rem;
}

.icon {
  color: white !important;
}

.icon-collapsed {
  color: #9396A0 !important;
}

.collapse-expand-button {
  margin-right: 20px;
}

.ungroup-button {
  position: absolute;
  top: -10px;
  right: -20px;
  z-index: 92; /* larger than vue-resizable borders */
}
</style>
